import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CloudOff, ShieldCheck, Clock, Leaf, AlertTriangle, ArrowRight, Activity, Zap, ShieldAlert, Ban } from 'lucide-react';

export default function ProintecaVsTradicional() {
  const [activeTab, setActiveTab] = useState(0);

  // Detectar idioma
  const isEn = typeof document !== 'undefined' && document.documentElement.lang === 'en';

  const comparisons = [
    {
      id: 'co2',
      icon: CloudOff,
      metric: isEn ? "CO₂ Emissions" : "Emisiones de CO₂",
      traditional: {
        title: isEn ? "Traditional Method" : "Método Tradicional",
        desc: isEn 
          ? "Flowlines vented directly into the atmosphere for depressurization." 
          : "Líneas de flujo venteadas directamente a la atmósfera para despresurización.",
        stat: isEn ? "High Pollution" : "Alta Contaminación",
        icon: AlertTriangle,
        color: "red"
      },
      prointeca: {
        title: isEn ? "The Prointeca Standard" : "El Estándar Prointeca",
        desc: isEn 
          ? "Minimization of direct venting through hermetic by-pass and hot tapping technologies." 
          : "Reducción al mínimo del venteo directo mediante tecnologías de by-pass hermético y derivación en caliente.",
        stat: isEn ? "0% Emissions" : "0% Emisiones",
        icon: ShieldCheck,
        color: "emerald"
      },
      impact: isEn ? "Total reduction of operational carbon footprint." : "Reducción total de la huella de carbono operativa."
    },
    {
      id: 'derrames',
      icon: ShieldCheck,
      metric: isEn ? "Spill Risk" : "Riesgo de Derrames",
      traditional: {
        title: isEn ? "Traditional Method" : "Método Tradicional",
        desc: isEn 
          ? "High environmental risk due to conventional welding without thermal control and single gaskets." 
          : "Alto riesgo ambiental por soldaduras convencionales sin control térmico y empaques simples.",
        stat: isEn ? "Critical Vulnerability" : "Vulnerabilidad Crítica",
        icon: ShieldAlert,
        color: "red"
      },
      prointeca: {
        title: isEn ? "The Prointeca Standard" : "El Estándar Prointeca",
        desc: isEn 
          ? "Minimal operational leaks secured with double-barrier redundant mechanical seals." 
          : "Mínimas fugas operativas aseguradas con sellos mecánicos redundantes de doble barrera.",
        stat: isEn ? "Secure Double Barrier" : "Doble Barrera Segura",
        icon: ShieldCheck,
        color: "emerald"
      },
      impact: isEn ? "Absolute protection of environment and assets." : "Protección absoluta del entorno y activos."
    },
    {
      id: 'interrupcion',
      icon: Clock,
      metric: isEn ? "Operational Shutdown" : "Interrupción Operativa",
      traditional: {
        title: isEn ? "Traditional Method" : "Método Tradicional",
        desc: isEn 
          ? "Complete plant shutdown for 15+ days for general maintenance and depressurization." 
          : "Parada total de planta por 15+ días para mantenimiento general y despresurización.",
        stat: isEn ? "15+ Days of Shutdown" : "15+ Días de Parada",
        icon: Ban,
        color: "red"
      },
      prointeca: {
        title: isEn ? "The Prointeca Standard" : "El Estándar Prointeca",
        desc: isEn 
          ? "100% flow continuity throughout the entire cutting and hot tapping process." 
          : "Continuidad del flujo al 100% durante todo el proceso de corte y derivación.",
        stat: isEn ? "0 Days of Shutdown" : "0 Días de Parada",
        icon: Zap,
        color: "emerald"
      },
      impact: isEn ? "Millions of dollars saved in continuity." : "Millones de dólares ahorrados en continuidad."
    },
    {
      id: 'ecosistemas',
      icon: Leaf,
      metric: isEn ? "Ecosystem Impact" : "Impacto en Ecosistemas",
      traditional: {
        title: isEn ? "Traditional Method" : "Método Tradicional",
        desc: isEn 
          ? "Affectation of soils and aquifers due to lack of comprehensive HSEQ preventive plans." 
          : "Afectación de suelos y acuíferos por carencia de planes preventivos de HSEQ integrales.",
        stat: isEn ? "Environmental Damage" : "Daño Ambiental",
        icon: AlertTriangle,
        color: "red"
      },
      prointeca: {
        title: isEn ? "The Prointeca Standard" : "El Estándar Prointeca",
        desc: isEn 
          ? "Immediate environmental remediation and certified ecological restoration of impacted areas." 
          : "Saneamiento ambiental inmediato y restauración ecológica certificada de áreas impactadas.",
        stat: isEn ? "100% Remediation" : "Saneamiento 100%",
        icon: Leaf,
        color: "emerald"
      },
      impact: isEn ? "Absolute commitment to the environment." : "Compromiso absoluto con el medio ambiente."
    }
  ];

  const activeData = comparisons[activeTab];

  return (
    <section className="py-24 bg-[#030712] relative overflow-hidden border-b border-white/5">
      {/* Background Decor */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#007AFF]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        
        {/* Encabezado */}
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-white font-semibold text-xs mb-6 uppercase tracking-widest backdrop-blur-md">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span>{isEn ? "Technical & Operational Difference" : "Diferencia Técnica & Operativa"}</span>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight tracking-tight">
            {isEn ? (
              <>We are not an alternative. <br className="hidden md:block" /> We are <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">The New Standard</span>.</>
            ) : (
              <>No somos una alternativa. <br className="hidden md:block" /> Somos <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">El Nuevo Estándar</span>.</>
            )}
          </h2>
          <p className="text-gray-400 text-lg md:text-xl font-light leading-relaxed">
            {isEn 
              ? "Discover the chasm between obsolete industrial maintenance and our precision engineering. Risk minimization, minimal emissions, and guaranteed operational continuity."
              : "Descubra el abismo entre el mantenimiento industrial obsoleto y nuestra ingeniería de precisión. Minimización de riesgos, mínimas emisiones y continuidad operativa garantizada."
            }
          </p>
        </div>

        {/* Contenedor Interactivo */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Navegación Izquierda */}
          <div className="lg:col-span-4 flex flex-col gap-3">
            {comparisons.map((comp, idx) => {
              const Icon = comp.icon;
              const isActive = activeTab === idx;
              return (
                <button
                  key={comp.id}
                  onClick={() => setActiveTab(idx)}
                  className={`relative flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 text-left border overflow-hidden ${
                    isActive 
                      ? 'bg-[#0A192F] border-emerald-500/50 shadow-[0_0_30px_rgba(16,185,129,0.15)]' 
                      : 'bg-[#050D19]/50 border-white/5 hover:border-white/10 hover:bg-[#071324]'
                  }`}
                >
                  {isActive && (
                    <motion.div 
                      layoutId="activeTabIndicator"
                      className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 shadow-[0_0_15px_#10B981]" 
                    />
                  )}
                  <div className={`p-3 rounded-xl transition-colors ${isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-gray-500'}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <span className={`block font-bold text-lg ${isActive ? 'text-white' : 'text-gray-400'}`}>
                      {comp.metric}
                    </span>
                    <span className={`text-xs font-semibold tracking-wider uppercase mt-1 ${isActive ? 'text-emerald-300' : 'text-gray-400'}`}>
                      {isActive ? (isEn ? "Active Metric" : "Métrica Activa") : (isEn ? "View Comparison" : "Ver Comparativa")}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Panel de Visualización Dinámico Derecha */}
          <div className="lg:col-span-8 relative">
            <div className="relative bg-[#071324]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-10 overflow-hidden min-h-[400px] flex flex-col justify-center">
              
              {/* Overlay Decorativo */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none mix-blend-overlay" />
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -20, filter: 'blur(10px)' }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="relative z-10"
                >
                  {/* Etiqueta Superior de Impacto */}
                  <div className="flex justify-center mb-10">
                    <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full border border-[#007AFF]/30 bg-[#007AFF]/10">
                      <Zap className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400 font-bold text-sm tracking-widest uppercase">{isEn ? "Real Impact" : "Impacto Real"}: {activeData.impact}</span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-stretch relative">
                    
                    {/* Tarjeta Tradicional */}
                    <div className="relative p-6 md:p-8 rounded-2xl bg-gradient-to-b from-red-950/20 to-transparent border border-red-500/20 group flex flex-col items-center text-center">
                      <div className="mb-6 flex justify-center w-full">
                        <span className="inline-flex items-center text-[10px] font-black text-red-400 uppercase tracking-widest px-3 py-1 rounded-full border border-red-500/20 bg-red-500/10">
                          {isEn ? "Obsolete Method" : "Método Obsoleto"}
                        </span>
                      </div>
                      
                      <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6 border border-red-500/20 group-hover:scale-110 transition-transform duration-300">
                        <activeData.traditional.icon className="w-8 h-8 text-red-500 opacity-80" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-300 mb-4">{activeData.traditional.title}</h3>
                      <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 flex-grow">
                        {activeData.traditional.desc}
                      </p>
                      <div className="w-full mt-auto px-4 py-3 bg-red-500/5 rounded-xl border border-red-500/10">
                        <span className="text-red-400 font-bold uppercase tracking-widest text-xs">
                          {activeData.traditional.stat}
                        </span>
                      </div>
                    </div>

                    {/* VS Badge */}
                    <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#050D19] border border-white/10 items-center justify-center z-20 shadow-2xl">
                      <div className="w-10 h-10 rounded-full border border-white/5 bg-white/5 flex items-center justify-center">
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>

                    {/* Tarjeta Prointeca */}
                    <div className="relative p-6 md:p-8 rounded-2xl bg-gradient-to-b from-[#0A192F]/80 to-[#050D19] border border-emerald-500/30 overflow-hidden shadow-[0_0_40px_rgba(16,185,129,0.1)] flex flex-col items-center text-center group">
                      
                      {/* Efectos de Brillo Prointeca */}
                      <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 blur-[60px] pointer-events-none transition-opacity duration-500 group-hover:opacity-100 opacity-60" />
                      <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#007AFF]/10 blur-[60px] pointer-events-none transition-opacity duration-500 group-hover:opacity-100 opacity-60" />
                      
                      <div className="mb-6 flex justify-center w-full relative z-10">
                        <span className="inline-flex items-center text-[10px] font-black text-emerald-400 uppercase tracking-widest px-4 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                          {isEn ? "The New Standard" : "El Nuevo Estándar"}
                        </span>
                      </div>
                      
                      <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-6 border border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.2)] relative z-10 group-hover:scale-110 transition-transform duration-300">
                        <activeData.prointeca.icon className="w-10 h-10 text-emerald-400" />
                      </div>
                      <h3 className="text-2xl font-black text-white mb-4 relative z-10">
                        {activeData.prointeca.title.includes("Prointeca") ? (
                          <>
                            {activeData.prointeca.title.replace("Prointeca", "")}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] via-[#FFB800] to-[#FFD700] uppercase font-black tracking-wide">
                              PROINTECA
                            </span>
                          </>
                        ) : activeData.prointeca.title.includes("Prointeca") ? (
                          <>
                            {activeData.prointeca.title.replace("Prointeca", "")}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] via-[#FFB800] to-[#FFD700] uppercase font-black tracking-wide">
                              PROINTECA
                            </span>
                          </>
                        ) : (
                          activeData.prointeca.title
                        )}
                      </h3>
                      <p className="text-emerald-50/80 text-sm md:text-base leading-relaxed mb-6 font-medium flex-grow relative z-10">
                        {activeData.prointeca.desc}
                      </p>
                      <div className="w-full mt-auto px-4 py-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-xl border border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.1)] relative z-10">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400 font-black uppercase tracking-widest text-sm drop-shadow-sm">
                          {activeData.prointeca.stat}
                        </span>
                      </div>
                    </div>

                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
